import { Link } from 'react-router-dom';
import { SiteHeader, type SiteHeaderLink } from '../components/SiteHeader';
import { CHROME_EXTENSION_STORE_URL } from '../constants/chromeExtension';
import styles from './Landing.module.css';

const HEADER_LINKS: SiteHeaderLink[] = [
  { label: 'Docs', to: '/docs' },
  { label: 'Extension', href: CHROME_EXTENSION_STORE_URL, external: true },
  { label: 'Open wallet', to: '/wallet', variant: 'cta' },
];

const ASSETS_BASE = '/assets';

/* Proof chips: facts about the wallet and the network it targets (no claims about audits or mainnet). */
const CHIPS = ['Non-custodial', 'Ed25519 keys', 'Web + browser extension', 'Public testnet'] as const;

export function Landing() {
  return (
    <div className={`${styles.page} page-landing`}>
      <div className={styles.pageContent}>
      <SiteHeader links={HEADER_LINKS} />

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroVisual}>
            <div className={styles.heroMascotWrap}>
              <img
                src={`${ASSETS_BASE}/mascot-excited.webp`}
                alt="Boing Bot — Boing Network mascot"
                className={styles.heroMascotImg}
              />
            </div>
          </div>
          <h1 className={styles.heroTitle}>Boing Express</h1>
          <p className={styles.heroTagline}>Your gateway to Boing</p>
          <p className={styles.stageBadge} data-testid="landing-testnet-label">Public testnet — mainnet not live</p>
          <div className={styles.heroPills}>
            {CHIPS.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <p className={styles.heroDesc}>
            Boing Express is a non-custodial wallet designed for the Boing ecosystem. Create or import a wallet,
            get testnet BOING, send and receive, and connect to apps from the browser or the extension.
            Your keys never leave your device.
          </p>
          <div className={styles.heroActions}>
            <Link to="/wallet" className={styles.primaryBtn}>Get started</Link>
            <a
              href={CHROME_EXTENSION_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.secondaryBtn}
            >
              Add to Chrome
            </a>
            <Link to="/docs" className={styles.secondaryBtn}>Documentation</Link>
          </div>
        </section>

        <section className={styles.features} aria-labelledby="first-steps-title">
          <div className="section-header">
            <span className="section-eyebrow">New here?</span>
            <h2 id="first-steps-title" className="section-title">First steps</h2>
          </div>
          <ol style={{ maxWidth: 640, margin: '0 auto', lineHeight: 1.7 }}>
            <li><Link to="/wallet">Create a wallet</Link> and save your private key backup.</li>
            <li>Get free testnet BOING in the wallet&apos;s Faucet tab or at <a href="https://boing.network/faucet" target="_blank" rel="noopener noreferrer">boing.network/faucet</a>.</li>
            <li>Send BOING, then see what happened on-chain in <a href="https://boing.observer" target="_blank" rel="noopener noreferrer">Boing Observer</a>.</li>
            <li>To connect to apps like <a href="https://boing.finance" target="_blank" rel="noopener noreferrer">Boing Finance</a>, <a href={CHROME_EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer">add the Chrome extension</a> (desktop Chrome, Edge or Brave).</li>
          </ol>
        </section>

        <section className={styles.features}>
          <div className="section-header">
            <span className="section-eyebrow">Why Boing Express</span>
            <h2 className="section-title">Your keys. Your account on Boing.</h2>
            <p className="section-subtitle">Everything you need to use Boing, and nothing you don&apos;t.</p>
          </div>
          <div className={styles.featureGrid}>
            <div className={styles.card}>
              <img src={`${ASSETS_BASE}/pillar-security.svg`} alt="" className={styles.cardIconImg} aria-hidden />
              <h3>Non-custodial</h3>
              <p>Keys are generated and stored only on your device. Password-encrypted; we never see your private key.</p>
            </div>
            <div className={styles.card}>
              <img src={`${ASSETS_BASE}/pillar-scalability.svg`} alt="" className={styles.cardIconImg} aria-hidden />
              <h3>Web & extension</h3>
              <p>
                Use the wallet at boing.express or{' '}
                <a href={CHROME_EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer">
                  install the Chrome extension
                </a>{' '}
                for one-click access from your browser toolbar.
              </p>
            </div>
            <div className={styles.card}>
              <img src={`${ASSETS_BASE}/pillar-decentralization.svg`} alt="" className={styles.cardIconImg} aria-hidden />
              <h3>Built for Boing</h3>
              <p>Native support for Boing: Ed25519 addresses, send and receive BOING, testnet faucet. Mainnet is not live.</p>
            </div>
          </div>
        </section>

        <section className={styles.cta}>
          <h2 className={styles.sectionTitle}>Ready to start?</h2>
          <p className={styles.ctaText}>Create a new wallet or import an existing one.</p>
          <Link to="/wallet" className={styles.primaryBtn}>Open wallet</Link>
        </section>

        <footer className={styles.footer}>
          <h3 className={styles.footerTitle}>Resources</h3>
          <div className={styles.footerLinks}>
            <a href={CHROME_EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer">
              Chrome Web Store
            </a>
            <a href="https://boing.network/testnet/join" target="_blank" rel="noopener noreferrer">Join Testnet</a>
            <a href="https://boing.network/faucet" target="_blank" rel="noopener noreferrer">Faucet</a>
            <a href="https://boing.network" target="_blank" rel="noopener noreferrer">Boing Network</a>
            <a href="https://discord.gg/K3w9nbUw4N" target="_blank" rel="noopener noreferrer">Discord</a>
            <a href="https://boing.network/docs/rpc-api" target="_blank" rel="noopener noreferrer">RPC API</a>
            <Link to="/docs">Documentation</Link>
            <Link to="/support">Support</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
          <p className={styles.footerCopy}>Boing Express · boing.express · Part of Boing Network — Quality, enforced by the protocol.</p>
        </footer>
      </main>
      </div>
    </div>
  );
}
